import React from 'react';
import { motion } from 'framer-motion';
import { SolutionSectionProps } from '@/types/components';
import { Brain, Users, Laptop, Workflow, Settings, LineChart, Target } from 'lucide-react';

const SOLUTIONS = [
  {
    title: "AI-Powered Efficiency",
    description: "Solvrays leverages advanced AI to streamline complex insurance workflows, reducing manual intervention and increasing operational speed.",
    icon: "Brain"
  },
  {
    title: "Human-Centered Design",
    description: "Built with the end-user in mind, Solvrays focuses on simplifying the user experience for internal operations staff, third-party stakeholders such as agents, and customers such as policyholders, beneficiaries, etc.",
    icon: "Users"
  },
  {
    title: "Legacy Modernization",
    description: "Integrates seamlessly with existing legacy systems, offering a pathway to dismantle legacy systems overtime.",
    icon: "Laptop"
  },
  {
    title: "Workflow-Driven Innovation",
    description: "Automates routine tasks and optimizes business processes, freeing up valuable time for staff to focus on higher-value activities.",
    icon: "Workflow"
  },
  {
    title: "Configurable & Scalable",
    description: "A modular solution tailored to meet the specific needs of insurance carriers of all sizes, capable of scaling as business demands grow.",
    icon: "Settings"
  },
  {
    title: "Enhanced Decision-Making",
    description: "Real-time analytics and AI-driven insights help organizations make data-backed decisions, improving accuracy and reducing risk.",
    icon: "LineChart"
  },
  {
    title: "Purpose-Driven Impact",
    description: "Aligns technological advancements with a mission to create meaningful change and a better experience for all stakeholders in the insurance ecosystem.",
    icon: "Target"
  }
];

const IconMap = {
  Brain,
  Users,
  Laptop,
  Workflow,
  Settings,
  LineChart,
  Target
};

const SolutionSection: React.FC<SolutionSectionProps> = ({ className }) => {
  return (
    <section id="solution" className="py-20 bg-gradient-to-b from-blue-900 to-emerald-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Our Solution
          </motion.h2>
          <motion.p
            className="text-2xl text-gray-400"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Transforming Insurance Operations
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SOLUTIONS.map((solution, index) => {
            const Icon = IconMap[solution.icon as keyof typeof IconMap];
            return (
              <motion.div
                key={solution.title}
                className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Icon className="w-12 h-12 text-emerald-400 mb-6" />
                <h3 className="text-2xl md:text-3xl font-semibold mb-4 text-white">
                  {solution.title}
                </h3>
                <p className="text-xl text-gray-400">
                  {solution.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
